import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useModal from '../hooks/useModal';
import CustomModal from '../common/CustomModal';
import PrimaryButton from '../buttons/PrimaryButton';
import useFetch from '../hooks/useFetch';
import { useNavigate, useParams } from 'react-router';
import { useContext, useEffect, useState } from 'react';
import SnackbarContext from '../contexts/SnackbarContext';
import UserContext from '../contexts/UserContext';
import { paymentSettingsRoute } from '../config/pageRoutes';
import StandardTextArea from '../inputs/StandardTextArea';
import { menuHeadersRoute } from '../config/apiRoutes';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExclusiveStarIcon from '../common/ExclusiveStarIcon';
import ResponsiveModal from '../modal/ResponsiveModal';

type Props = {
    restaurantShortName: string | undefined;
}

interface MenuHeader {
    header: string;
    footer: string;
}

const MenuHeaderEditorButton = ({ restaurantShortName }: Props) => {
    const { t } = useTranslation();
    const { lang } = useParams();
    const menuHeaderEditorModal = useModal();
    const caller = useFetch();
    const snackbar = useContext(SnackbarContext);
    const { hasFeature } = useContext(UserContext);
    const navigate = useNavigate()
    const [menuHeaders, setMenuHeaders] = useState<MenuHeader>({
        header: '',
        footer: '',
    });

    useEffect(() => {
        if (menuHeaderEditorModal.isOpen && hasFeature("MENU_HEADERS")) {
            fetchMenuHeaders();
        }
    }, [restaurantShortName, menuHeaderEditorModal.isOpen])

    async function fetchMenuHeaders() {
        if (!restaurantShortName) return;
        const url = menuHeadersRoute.replace(':restaurantShortName', restaurantShortName);
        const response = await caller.call("GET", url);
        if (response.ok) {
            setMenuHeaders(response.data as MenuHeader);
        } else {
            if (response.status === 403) {
                snackbar.showMessage(t("menu.menu_header_permission_error"), "error");
            } else {
                snackbar.showMessage(t("menu.menu_header_generic_error"), "error");
            }
        }
    }

    async function saveMenuHeaders() {
        if (!restaurantShortName) return;
        const url = menuHeadersRoute.replace(':restaurantShortName', restaurantShortName);
        const response = await caller.call("PUT", url, menuHeaders);
        if (response.ok) {
            snackbar.showMessage(t("menu.menu_header_save_success"), "success");
            menuHeaderEditorModal.close();
        } else {
            if (response.status === 403) {
                snackbar.showMessage(t("menu.menu_header_permission_error"), "error");
            } else {
                snackbar.showMessage(t("menu.menu_header_save_error"), "error");
            }
        }
    }

    function handleMenuHeaderEditorClick() {
        menuHeaderEditorModal.open();
    }

    async function handleMenuHeaderSave() {
        if (!restaurantShortName) return;
        await saveMenuHeaders();
    }

    const redirectToPayment = () => {
        navigate(paymentSettingsRoute.path.replace(':lang', lang || 'en'));
    };

    function handleHeaderTextChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
        setMenuHeaders({ ...menuHeaders, header: event.target.value });
    }

    function handleFooterTextChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
        setMenuHeaders({ ...menuHeaders, footer: event.target.value });
    }

    return (
        <>
            <PrimaryButton
                variant="outlined"
                onClick={handleMenuHeaderEditorClick}
                startIcon={<EditNoteIcon />}
                endIcon={
                    <div className="relative w-full h-full">
                        <ExclusiveStarIcon className="absolute -top-3 -right-5 text-white" />
                    </div>
                }
            >
                {t("menu.menu_headers")}
            </PrimaryButton>
            <ResponsiveModal
                open={menuHeaderEditorModal.isOpen}
                handleClose={menuHeaderEditorModal.close}
            >
                {hasFeature("MENU_HEADERS") ? (
                    <Box px={2} pt={1} pb={2} display="flex" flexDirection="column" gap={2}>
                        <div
                            className="font-bold text-center text-2xl text-primary-dark"
                        >
                            {t("menu.menu_headers")}
                        </div>
                        <div className="">
                            {t("menu.menu_header_editor_title")}
                        </div>
                        <div>
                            <StandardTextArea
                                label={t("menu.menu_header")}
                                value={menuHeaders.header}
                                placeholder={t("menu.menu_header_placeholder")}
                                onChange={handleHeaderTextChange}
                            />
                        </div>
                        <div>
                            <StandardTextArea
                                label={t("menu.menu_footer")}
                                value={menuHeaders.footer}
                                placeholder={t("menu.menu_footer_placeholder")}
                                onChange={handleFooterTextChange}
                            />
                        </div>
                        <PrimaryButton
                            onClick={handleMenuHeaderSave}
                            fullWidthOnMobile
                        >
                            {t("common.save")}
                        </PrimaryButton>
                    </Box>
                ) : (
                    <Box textAlign="center" mt={4}>
                        <p className="text-lg mb-4">
                            {t("menu.menu_header_feature_not_available")}
                        </p>
                        <PrimaryButton
                            variant="primary"
                            onClick={redirectToPayment}
                        >
                            {t('menu.change_subscription_plan')}
                        </PrimaryButton>
                    </Box>
                )}
            </ResponsiveModal>
            <CustomModal
                isOpen={false}
                handleClose={menuHeaderEditorModal.close}
                title={t("menu.menu_headers")}
            >

            </CustomModal>
        </>
    )
}

export default MenuHeaderEditorButton